import React from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FeedbackRowForOneOnOneSidebar } from 'screens/home/summary/components/Cards/Feedback/FeedbackProfileSummaryCard';
import { useMySummaryFeedback } from 'screens/home/summary/components/Cards/Feedback/hooks';
import { useOneOnOneInstanceDetails } from 'screens/oneOnOneRabbit/InstancePage/hooks/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { FeedbackIconDualTone, Plus } from 'assets/assetsV1/iconComponents';
import NoFeedbackImage from 'assets/assetsV1/illustrations/noFeedbacks.svg';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';

import { Button } from 'uiToolkitV1/Button';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SidebarCardTemplate } from '../common/components/SidebarCardTemplate';

const FeedbackSection = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();

  const {
    receiverEmployee: { id: employeeId, displayName },
  } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const isLoggedInEmployeeTheReceiver = Number(loggedInEmployeeId) === employeeId;

  const { data, isLoading } = useMySummaryFeedback({
    limit: 50,
    offset: 0,
    employeeId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const history = useHistory();

  const handleShareNewFeedbackClick = () => {
    // @ts-expect-error fix this
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: employeeId,
      // source: cohortIdsToSendMixpanelEventsFor.includes(sideNavSelectedCohort?.id)
      //   ? sideNavSelectedCohort?.name
      //   : null
    });
  };

  const handleRequestNewFeedbackClick = () => {
    // @ts-expect-error fix this

    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: employeeId ?? null,
      // source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK
    });
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!data?.length) {
    return (
      <SidebarCardTemplate label="Feedback" Icon={FeedbackIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoFeedbackImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>There is no feedback yet</TextBodyMedium>
            <TextBodyMedium color="text.g2">
              {isLoggedInEmployeeTheReceiver
                ? 'Go ahead and request feedback from your peers'
                : `Go ahead and share feedback to ${displayName}`}
            </TextBodyMedium>
          </Flex>
        </Flex>
        <Button
          onClick={() => {
            if (isLoggedInEmployeeTheReceiver) {
              handleRequestNewFeedbackClick();
            } else {
              handleShareNewFeedbackClick();
            }
          }}
          StartIcon={Plus}
          alignSelf="center"
          mt="22px"
        >
          {isLoggedInEmployeeTheReceiver ? 'Request Feedback' : 'Add Feedback'}
        </Button>
      </SidebarCardTemplate>
    );
  }

  return (
    <SidebarCardTemplate
      label="Feedback"
      Icon={FeedbackIconDualTone}
      // eslint-disable-next-line react/no-unstable-nested-components
      AddButton={() => (
        <Button
          onClick={() => {
            if (isLoggedInEmployeeTheReceiver) {
              handleRequestNewFeedbackClick();
            } else {
              handleShareNewFeedbackClick();
            }
          }}
          StartIcon={Plus}
          variant="outlined"
          data-testid="sidebar-add-new-feedback-button"
        />
      )}
    >
      {data?.map(
        (
          {
            requesterMiniProfile,
            requestTextDetails,
            timestamp,
            feedbackStatus,
            sentiment,
            continuousFeedbackId,
            postId,
            providerMiniProfile,
            postTextDetails,
          },
          index
        ) => (
          <Box sx={{ borderRadius: '8px', background: 'white', margin: '4px 0px' }}>
            <FeedbackRowForOneOnOneSidebar
              requesterMiniProfile={requesterMiniProfile}
              requestTextDetails={requestTextDetails}
              timestamp={timestamp}
              key={postId}
              feedbackStatus={feedbackStatus}
              sentiment={sentiment}
              continuousFeedbackId={continuousFeedbackId}
              postId={postId}
              providerMiniProfile={providerMiniProfile}
              postTextDetails={postTextDetails}
              // eslint-disable-next-line no-unsafe-optional-chaining
              showDivider={data?.data?.length - 1 !== index}
            />
          </Box>
        )
      )}
    </SidebarCardTemplate>
  );
};

export { FeedbackSection };
